import React, { useState } from 'react';
import { downloadFile } from '../lib/download';
import { isViewOnly } from '../lib/media';
import MediaViewer from './MediaViewer';

const noContextMenu = (e) => e.preventDefault();

function timeLabel(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Delivery state for our own messages: sending / sent (✓) / delivered (✓✓) /
// read (✓✓ blue) / failed.
function Ticks({ status }) {
  if (status === 'sending') return <span className="text-[10px] opacity-70">🕓</span>;
  if (status === 'failed') return <span className="text-[10px] text-red-200">!</span>;
  if (status === 'read') return <span className="text-[11px] text-sky-200">✓✓</span>;
  if (status === 'delivered') return <span className="text-[11px] opacity-80">✓✓</span>;
  return <span className="text-[11px] opacity-80">✓</span>;
}

// Highlight occurrences of `term` inside text (Phase 6 in-chat search).
function Highlighted({ text, term }) {
  if (!term) return <>{text}</>;
  const lower = text.toLowerCase();
  const q = term.toLowerCase();
  const parts = [];
  let i = 0;
  let idx = lower.indexOf(q);
  while (idx !== -1) {
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(
      <mark key={idx} className="bg-yellow-300 text-ink rounded px-0.5">
        {text.slice(idx, idx + q.length)}
      </mark>
    );
    i = idx + q.length;
    idx = lower.indexOf(q, i);
  }
  if (i < text.length) parts.push(text.slice(i));
  return <>{parts}</>;
}

export default function MessageBubble({ message, showSender, highlight, onDelete }) {
  const { mine, text, fileUrl, fileName, type, senderName, createdAt, status } = message;
  const isImage = fileUrl && type === 'image';
  const isVideo = fileUrl && type === 'video';
  // A non-media attachment. Show the row as soon as we know the name (even while
  // an outgoing file is still uploading, when fileUrl isn't set yet).
  const isOtherFile = (fileUrl || fileName) && type !== 'image' && type !== 'video';
  // Sender chose "View & Print only". `locked` = it's someone else's, so no
  // download / right-click save / drag-to-save; open in the in-app viewer.
  const viewOnly = isViewOnly(message);
  const locked = viewOnly && !mine;
  const [viewerOpen, setViewerOpen] = useState(false);

  const download = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileUrl && !locked) downloadFile(fileUrl, fileName);
  };

  const openViewer = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (fileUrl && !message.optimistic) setViewerOpen(true);
  };

  const lockProps = locked
    ? { draggable: false, onContextMenu: noContextMenu, onDragStart: noContextMenu }
    : {};

  return (
    <div className={`flex group items-center gap-1 ${mine ? 'justify-end' : 'justify-start'}`}>
      {/* Delete affordance — appears on hover, only for deletable messages. */}
      {onDelete && (
        <button
          onClick={onDelete}
          title="Delete message"
          className="order-first shrink-0 w-7 h-7 rounded-full grid place-items-center text-muted opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-red-500"
        >
          🗑
        </button>
      )}
      <div
        className={`max-w-[72%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
          mine
            ? 'bg-brand text-white rounded-br-md'
            : 'bg-white text-ink rounded-bl-md border border-slate-100'
        }`}
      >
        {showSender && senderName && (
          <div className="text-xs font-semibold text-brand mb-0.5">{senderName}</div>
        )}

        {(isImage || isVideo) && (
          <div className={`relative mb-1 ${locked ? 'select-none' : ''}`} {...lockProps}>
            {isImage ? (
              <img
                src={fileUrl}
                alt={fileName || ''}
                onClick={openViewer}
                className="rounded-lg max-h-72 object-cover cursor-zoom-in"
                {...lockProps}
              />
            ) : (
              <video
                src={fileUrl}
                controls
                controlsList={locked ? 'nodownload noremoteplayback' : undefined}
                disablePictureInPicture={locked}
                className="rounded-lg max-h-72"
                {...lockProps}
              />
            )}
            {viewOnly && (
              <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full bg-black/55 text-white text-[10px]">
                🔒 View only
              </span>
            )}
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {isVideo && (
                <button
                  onClick={openViewer}
                  title="Open"
                  className="w-8 h-8 rounded-full bg-black/55 text-white grid place-items-center hover:bg-black/75"
                >
                  ⤢
                </button>
              )}
              {!locked && (
                <button
                  onClick={download}
                  title="Download"
                  className="w-8 h-8 rounded-full bg-black/55 text-white grid place-items-center hover:bg-black/75"
                >
                  ⬇
                </button>
              )}
            </div>
          </div>
        )}

        {isOtherFile && (
          <div className={`flex items-center gap-2 mb-1 ${mine ? 'text-white' : 'text-ink'}`}>
            <span className="text-lg">{viewOnly ? '🔒' : '📎'}</span>
            <span className="truncate flex-1">
              {fileName || 'Attachment'}
              {viewOnly && (
                <span className={`block text-[10px] ${mine ? 'text-white/80' : 'text-muted'}`}>
                  View only
                </span>
              )}
            </span>
            {fileUrl && !message.optimistic && (
              <button
                onClick={openViewer}
                title="View / print"
                className={`shrink-0 px-2 py-0.5 rounded text-xs ${
                  mine ? 'bg-white/20 hover:bg-white/30' : 'bg-brand/10 text-brand hover:bg-brand/20'
                }`}
              >
                👁 View
              </button>
            )}
            {fileUrl && !locked && (
              <button
                onClick={download}
                title="Download"
                className={`shrink-0 px-2 py-0.5 rounded text-xs ${
                  mine ? 'bg-white/20 hover:bg-white/30' : 'bg-brand/10 text-brand hover:bg-brand/20'
                }`}
              >
                ⬇ Save
              </button>
            )}
          </div>
        )}

        {text && (
          <div className="whitespace-pre-wrap break-words">
            <Highlighted text={text} term={highlight} />
          </div>
        )}

        <div
          className={`flex items-center gap-1 justify-end mt-0.5 text-[10px] ${
            mine ? 'text-white/80' : 'text-muted'
          }`}
        >
          <span>{timeLabel(createdAt)}</span>
          {mine && <Ticks status={status} />}
        </div>
      </div>
      {viewerOpen && (
        <MediaViewer message={message} locked={locked} onClose={() => setViewerOpen(false)} />
      )}
    </div>
  );
}
